import React, { useEffect, useState } from 'react';
import './PaymentAbandonSurvey.css';
import {
  trackPaymentAbandonmentSurveyVisit,
  trackPaymentAbandonmentSurveyClosed,
  trackPaymentAbandonmentSurveyContinueClicked,
} from './userJourneyTracker';

const OTHER_OPTION = 'Something else, please specify';

const PAYMENT_ABANDON_SURVEY_OPTIONS = [
  'Want to see my video results first',
  'Not sure how to cancel',
  "Couldn't find my preferred payment option",
  'Just exploring',
  OTHER_OPTION,
];

/**
 * Shown when user closes Razorpay without completing payment (see PricingPage).
 */
export default function PaymentAbandonSurvey({ isOpen, onClose, idToken }) {
  const [selected, setSelected] = useState([]);
  const [other, setOther] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      trackPaymentAbandonmentSurveyVisit();
    }
  }, [isOpen]);

  const toggle = (opt) =>
    setSelected((prev) => (prev.includes(opt) ? prev.filter((x) => x !== opt) : [...prev, opt]));

  const otherSelected = selected.includes(OTHER_OPTION);
  const canSubmit =
    selected.length > 0 && (!otherSelected || String(other).trim().length > 0);

  /** @param {boolean} [fromSubmitSuccess] When true, skip "closed" journey (Continue flow already recorded). */
  const handleClose = (fromSubmitSuccess = false) => {
    if (!fromSubmitSuccess) {
      trackPaymentAbandonmentSurveyClosed();
    }
    setSelected([]);
    setOther('');
    setError('');
    onClose();
  };

  const submit = async () => {
    if (!canSubmit || !idToken) {
      handleClose(false);
      return;
    }
    trackPaymentAbandonmentSurveyContinueClicked();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/billing/payment-abandonment-survey', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reasons: selected,
          other: otherSelected ? String(other).trim() : '',
          timestamp: new Date().toISOString(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not send feedback');
      }
      handleClose(true);
    } catch (e) {
      setError(e?.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="payment-abandon-survey-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-abandon-survey-title"
    >
      <button
        type="button"
        className="payment-abandon-survey-backdrop"
        aria-label="Dismiss"
        onClick={handleClose}
      />
      <div className="payment-abandon-survey-dialog">
        <div className="payment-abandon-survey-header">
          <h2 id="payment-abandon-survey-title" className="payment-abandon-survey-title">
            Before you go what held you from making payment?
          </h2>
          <button
            type="button"
            className="payment-abandon-survey-close"
            onClick={handleClose}
            aria-label="Close"
            disabled={submitting}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M18 6L6 18M6 6l12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <p className="payment-abandon-survey-sub">
          Help us improve checkout. It&apos;ll just take a few clicks.
        </p>

        <div className="payment-abandon-survey-options">
          {PAYMENT_ABANDON_SURVEY_OPTIONS.map((opt) => (
            <label key={opt} className="payment-abandon-survey-option-card">
              <input
                type="checkbox"
                className="payment-abandon-survey-check"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
              />
              <span className="payment-abandon-survey-option-text">{opt}</span>
            </label>
          ))}
        </div>

        {otherSelected && (
          <textarea
            className="payment-abandon-survey-other"
            value={other}
            onChange={(e) => setOther(e.target.value)}
            placeholder="Tell us more…"
            rows={3}
          />
        )}

        {error && (
          <p className="payment-abandon-survey-error" role="alert">
            {error}
          </p>
        )}

        <div className="payment-abandon-survey-actions">
          <button
            type="button"
            className={`payment-abandon-survey-submit${submitting ? ' payment-abandon-survey-submit--loading' : ''}`}
            onClick={submit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? (
              <span className="payment-abandon-survey-submit-inner">
                <span className="payment-abandon-survey-submit-spinner" aria-hidden="true" />
                Sending…
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
